"use client";
import { getMediaUrl } from "@/lib/utils";
import Footer from "@/components/ar-IQ/Footer";
import Navbar from "@/components/ar-IQ/Navbar";
import servicesData from "@/data/services.json";
import { getServices, getServicesPage } from "@/lib/api";
import { motion, useInView } from "framer-motion";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";

function detectLanguage(str) {
  // Count Arabic characters
  const arabicChars = str.match(/[\u0600-\u06FF]/g) || [];
  // Count English characters
  const englishChars = str.match(/[a-zA-Z]/g) || [];
  // Compare counts
  if (arabicChars.length > englishChars.length) {
    return "ar";
  } else {
    return "en";
  }
}

const ServicesPage = () => {
  const heroRef = useRef(null);
  const [floatingShapes, setFloatingShapes] = useState([]);
  const [services, setServices] = useState([]);
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mounted, setMounted] = useState(false);

  const isHeroInView = useInView(heroRef, { once: true });

  useEffect(() => {
    setMounted(true);

    const shapes = Array.from({ length: 8 }, (_, i) => ({
      id: i,
      top: 10 + Math.random() * 80,
      left: 10 + Math.random() * 80,
      delay: Math.random() * 2,
      duration: 4 + Math.random() * 2,
    }));
    setFloatingShapes(shapes);

    const fetchServices = async () => {
      try {
        console.log("[ServicesPage] Fetching services data...");
        const [allServices, page] = await Promise.all([
          getServices("ar", false),
          getServicesPage("ar"),
        ]);
        console.log("[ServicesPage] Services received:", allServices.length);

        // Use backend data if available, otherwise fallback to static JSON
        if (Array.isArray(allServices) && allServices.length > 0) {
          const filtered = allServices.filter((s) => s && typeof s === 'object' && s.id);
          setServices(filtered);
        } else {
            // Fallback to static JSON (ignore non-service keys like mediaBase)
            console.log("[ServicesPage] Using static JSON fallback");
            const staticServices = Object.values(servicesData).filter(
              (s) => s && typeof s === "object" && s.id
            );
            setServices(staticServices);
        }

        setPageData(page);
      } catch (error) {
        console.error("[ServicesPage] Error fetching services:", error);
        // Fallback to static JSON (ignore non-service keys like mediaBase)
        console.log(
          "[ServicesPage] Error occurred, using static JSON fallback"
        );
        const staticServices = Object.values(servicesData).filter(
          (s) => s && typeof s === "object" && s.id
        );
        setServices(staticServices);
        setPageData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, []);

  if (!mounted) {
    return null;
  }

  if (loading) {
    return (
      <div
        className="min-h-screen bg-white flex items-center justify-center"
        dir="rtl"
      >
        <motion.div
          className="text-2xl font-bold text-[#0a367b]"
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 1.5, repeat: Number.POSITIVE_INFINITY }}
        >
          جاري التحميل...
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" dir="rtl">
      <Navbar />

      {/* Hero Section */}
      <motion.section
        ref={heroRef}
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
      >
        {/* Background Video */}
        <div className="absolute inset-0 will-change-transform">
          {pageData?.hero?.video ? (
            <video
              autoPlay
              muted
              loop
              playsInline
              preload="none"
              className="w-full h-full object-cover"
            >
              <source src={getMediaUrl(pageData.hero.video)} type="video/mp4" />
            </video>
          ) : (
            <video
              autoPlay
              muted
              loop
              playsInline
              preload="none"
              className="w-full h-full object-cover"
            >
              <source src={getMediaUrl('hero.mp4')} type="video/mp4" />
            </video>
          )}
          <div className="absolute inset-0 bg-gradient-to-br from-[#0a367b]/90 via-[#0a367b]/70 to-[#0a367b]/50"></div>
        </div>

        {/* Floating Particles */}
        {mounted && (
          <div className="absolute inset-0">
            {floatingShapes.map((shape) => (
              <motion.div
                key={shape.id}
                className="absolute w-2 h-2 bg-[#dfa428]/40 rounded-full"
                style={{
                  top: `${shape.top}%`,
                  left: `${shape.left}%`,
                }}
                animate={{
                  y: [0, -100, 0],
                  opacity: [0, 1, 0],
                  scale: [0, 1.5, 0],
                }}
                transition={{
                  duration: shape.duration,
                  repeat: Number.POSITIVE_INFINITY,
                  delay: shape.delay,
                }}
              />
            ))}
          </div>
        )}

        {/* Content */}
        <div className="relative z-10 container mx-auto px-6 text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.5 }}
            className="max-w-5xl mx-auto"
          >
            <motion.h1
              className="text-5xl md:text-7xl font-bold mb-8 leading-tight"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.2, delay: 1 }}
            >
              {loading
                ? "جاري التحميل..."
                : pageData?.hero?.title || "خدماتنا المتميزة"}
              {pageData?.hero?.titleHighlight && (
                <>
                  <br />
                  <span className="text-[#dfa428]">{pageData.hero.titleHighlight}</span>
                </>
              )}
            </motion.h1>

            <motion.p
              className="text-xl md:text-2xl leading-relaxed opacity-90 mb-12"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 1.5 }}
            >
              {loading
                ? ""
                : pageData?.hero?.subtitle ||
                  "نقدم مجموعة شاملة من الخدمات الإعلانية المبتكرة التي تلبي جميع احتياجاتك التسويقية وتضمن وصول رسالتك للجمهور المناسب بأكثر الطرق فعالية"}
            </motion.p>
          </motion.div>
        </div>

        {/* Geometric Background Elements */}
        <motion.div
          className="absolute top-20 right-20 w-32 h-32 bg-[#dfa428]/20 rounded-full"
          animate={{
            scale: [1, 1.3, 1],
            rotate: [0, 180, 360],
          }}
          transition={{ duration: 12, repeat: Number.POSITIVE_INFINITY }}
        />
        <motion.div
          className="absolute bottom-20 left-20 w-24 h-24 bg-white/10 rounded-full"
          animate={{
            scale: [1.2, 1, 1.2],
            rotate: [360, 180, 0],
          }}
          transition={{ duration: 8, repeat: Number.POSITIVE_INFINITY }}
        />
        <motion.div
          className="absolute top-1/3 left-1/4 w-16 h-16 bg-[#dfa428]/30 transform rotate-45"
          animate={{
            rotate: [45, 225, 405],
            scale: [1, 1.4, 1],
          }}
          transition={{ duration: 6, repeat: Number.POSITIVE_INFINITY }}
        />
      </motion.section>

      {/* Services Grid Section */}
      <section className="py-20 bg-gray-50 overflow-hidden relative">
        <div className="absolute inset-0 overflow-hidden">
          <motion.div
            className="absolute top-20 right-20 w-64 h-64 bg-gradient-to-br from-[#dfa428]/5 to-[#0a367b]/5 rounded-full"
            animate={{
              rotate: [0, 360],
              scale: [1, 1.2, 1],
              x: [0, 50, 0],
            }}
            transition={{ duration: 20, repeat: Number.POSITIVE_INFINITY }}
          />
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            className="text-center mb-20"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#0a367b] mb-6">
              {pageData?.serviceIntro?.title || "اختر الخدمة المناسبة لك"}
            </h2>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              {pageData?.serviceIntro?.subtitle ||
                "نقدم حلولاً متكاملة في مجال الإعلان والتسويق لضمان نجاح علامتك التجارية"}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
            {services.map((service, index) => (
              <ServiceCard key={service.id || service.slug} service={service} index={index} />
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

const ServiceCard = ({ service, index }) => {
  const cardRef = useRef(null);
  const isInView = useInView(cardRef, { once: true, margin: "-100px" });

  const resolveMedia = (val) => {
    if (!val) return "";
    if (typeof val !== "string") return getMediaUrl(val);
    const trimmed = val.trim();
    if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) return trimmed;
    if (/\.(mp4|webm|mov|avi|mkv|ogg)$/i.test(trimmed)) {
      return getMediaUrl(trimmed);
    }
    const base = servicesData?.mediaBase || "";
    return base ? base + encodeURI(trimmed) : getMediaUrl(trimmed);
  };

    const serviceImage =
      resolveMedia(service?.image) ||
      resolveMedia(service?.sections?.[0]?.images?.[0]) ||
      resolveMedia(service?.sections?.[0]?.image) ||
      "/hero.mp4";

  return (
    <motion.div
      ref={cardRef}
      className="group"
      initial={{ opacity: 0, y: 100 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay: index * 0.2 }}
    >
      <Link href={`/ar-IQ/services/${service.slug}`}>
        <motion.div
          className="bg-white rounded-3xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-500 cursor-pointer h-full"
          whileHover={{
            scale: 1.02,
            y: -10,
            transition: { duration: 0.3 },
          }}
        >
          {/* Image/Video Section */}
          <div className="relative h-64 lg:h-80 overflow-hidden">
            {serviceImage.endsWith(".mp4") ? (
              <video
                src={serviceImage}
                autoPlay
                muted
                loop
                playsInline
                className="w-full h-full object-cover"
              />
            ) : (
              <img
                src={serviceImage || "/placeholder.svg"}
                alt={service.title}
                className="w-full h-full object-cover"
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>

            {/* Service Number Badge */}
            <motion.div
              className="absolute top-6 right-6 bg-[#dfa428] text-[#0a367b] w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold shadow-lg"
              animate={{ rotate: [0, 360] }}
              transition={{
                duration: 20,
                repeat: Number.POSITIVE_INFINITY,
                ease: "linear",
              }}
            >
              {String(index + 1).padStart(2, "0")}
            </motion.div>

            {/* Hover Overlay */}
            <motion.div
              className="absolute inset-0  opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-center justify-center"
              initial={{ scale: 0.5 }}
              whileHover={{ scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              <motion.div
                className="text-white text-center"
                initial={{ y: 30, opacity: 0 }}
                whileHover={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.3, delay: 0.1 }}
              >
                <motion.div
                  className="text-2xl font-bold mb-4"
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 2, repeat: Number.POSITIVE_INFINITY }}
                >
                  استكشف الخدمة
                </motion.div>
                <motion.div
                  className="w-16 h-1 bg-[#dfa428] mx-auto rounded-full"
                  initial={{ width: 0 }}
                  whileHover={{ width: 64 }}
                  transition={{ duration: 0.3, delay: 0.2 }}
                />
              </motion.div>
            </motion.div>
          </div>

          {/* Content Section */}
          <div className="p-6 lg:p-8">
            <motion.h3
              className="text-xl lg:text-2xl font-bold text-[#0a367b] mb-4 leading-tight group-hover:text-yellow-600 transition-colors duration-300"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: index * 0.2 + 0.3 }}
            >
              {service.title}
            </motion.h3>

            <motion.p
              className="text-gray-600 leading-relaxed mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: index * 0.2 + 0.5 }}
            >
              {service.description}
            </motion.p>

            <motion.div
              className="flex items-center text-[#0a367b] font-semibold group-hover:text-yellow-600 transition-colors duration-300"
              initial={{ opacity: 0, x: -20 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ delay: index * 0.2 + 0.7 }}
            >
              <span className="ml-2">اعرف المزيد</span>
              <motion.span
                animate={{ x: [0, 5, 0] }}
                transition={{ duration: 1.5, repeat: Number.POSITIVE_INFINITY }}
              >
                ←
              </motion.span>
            </motion.div>
          </div>
        </motion.div>
      </Link>
    </motion.div>
  );
};

export default ServicesPage;
